import { CommonModule, DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, SecurityContext, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './landing.page.html',
  styleUrl: './landing.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingPage implements AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cdr = inject(ChangeDetectorRef);
  private injectedNodes: HTMLElement[] = [];

  protected landingMarkup = '';
  protected loadError = false;

  async ngAfterViewInit(): Promise<void> {
    try {
      const response = await fetch('/index-landing.html', { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Failed to load landing page (${response.status})`);
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // Sanitize the fetched markup instead of blindly trusting it. The landing
      // HTML is same-origin and developer-authored today, but sanitizing keeps it
      // safe if it ever becomes user/CMS-controlled (prevents stored XSS via innerHTML).
      this.landingMarkup = this.sanitizer.sanitize(SecurityContext.HTML, parsed.body.innerHTML) ?? '';
      this.loadError = false;
      this.cdr.markForCheck();

      queueMicrotask(() => {
        void this.hydrateStaticAssets(parsed);
      });
    } catch {
      this.loadError = true;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    for (const node of this.injectedNodes) {
      node.remove();
    }
    this.injectedNodes = [];
  }

  private async hydrateStaticAssets(parsed: Document): Promise<void> {
    this.injectStyles(parsed);
    await this.injectScripts(parsed);
    this.triggerLandingBootstrapEvents();
  }

  private injectStyles(parsed: Document): void {
    const links = Array.from(parsed.querySelectorAll('link[rel="stylesheet"]'));
    for (const link of links) {
      const href = link.getAttribute('href');
      if (!href || this.document.querySelector(`link[data-landing="true"][href="${href}"]`)) {
        continue;
      }
      const node = this.document.createElement('link');
      node.setAttribute('rel', 'stylesheet');
      node.setAttribute('href', href);
      node.setAttribute('data-landing', 'true');
      this.document.head.appendChild(node);
      this.injectedNodes.push(node);
    }
  }

  private async injectScripts(parsed: Document): Promise<void> {
    const scripts = Array.from(parsed.querySelectorAll('script'));
    for (const script of scripts) {
      const src = script.getAttribute('src');
      const type = script.getAttribute('type');

      // Avoid duplicate script injections when component re-initializes.
      if (src && this.document.querySelector(`script[data-landing="true"][src="${src}"]`)) {
        continue;
      }

      await this.appendScriptSequentially({
        src,
        type,
        text: src ? null : script.textContent ?? ''
      });
    }
  }

  private appendScriptSequentially(input: {
    src: string | null;
    type: string | null;
    text: string | null;
  }): Promise<void> {
    return new Promise((resolve) => {
      const node = this.document.createElement('script');
      node.setAttribute('data-landing', 'true');

      if (input.type) {
        node.setAttribute('type', input.type);
      }

      if (input.src) {
        node.setAttribute('src', input.src);
        node.onload = () => resolve();
        node.onerror = () => resolve();
      } else {
        node.text = input.text ?? '';
      }

      this.document.body.appendChild(node);
      this.injectedNodes.push(node);

      if (!input.src) {
        resolve();
      }
    });
  }

  private triggerLandingBootstrapEvents(): void {
    // The static landing bundle registers init handlers on DOMContentLoaded/load.
    // In Angular, scripts are injected after those events already fired, so we replay them.
    this.document.dispatchEvent(new Event('DOMContentLoaded'));
    window.dispatchEvent(new Event('load'));

    const lucide = (window as unknown as { lucide?: { createIcons: () => void } }).lucide;
    if (lucide?.createIcons) {
      lucide.createIcons();
    }
  }
}
